import { useTranslation } from "react-i18next";
import { SearchNormal1, Calendar2, Status } from "iconsax-react";
import { Select, Input, DatePicker } from "@/shared/ui";
import { FilterResetButton, ViewModeToggle } from "@/shared/components";
import { type Locale, type DateRange } from "@/shared/utils";

export interface StatusOption {
  label: string;
  value: string | null;
}

interface Props {
  search: string;
  onSearchChange: (v: string) => void;
  startDateRange: DateRange;
  onStartDateRangeChange: (r: DateRange) => void;
  statusFilter: string | null;
  onStatusChange: (v: string | null) => void;
  statusOptions: StatusOption[];
  hasActiveFilters: boolean;
  onReset: () => void;
  locale: Locale;
  view: "table" | "cards";
  onViewChange: (v: "table" | "cards") => void;
}

export default function ContractsFilter({
  search,
  onSearchChange,
  startDateRange,
  onStartDateRangeChange,
  statusFilter,
  onStatusChange,
  statusOptions,
  hasActiveFilters,
  onReset,
  locale,
  view,
  onViewChange,
}: Props) {
  const { t } = useTranslation("ContractsPage");

  function handleStartDateRangeChange(v: Date | { start: Date | null; end: Date | null } | null) {
    if (!v || v instanceof Date) return;
    onStartDateRangeChange(v);
  }

  return (
    <div className="flex items-center justify-between gap-2 mb-7">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="min-w-48">
          <Input
            isSmall
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("filters.searchPlaceholder")}
            icon={<SearchNormal1 size={14} color="currentColor" />}
          />
        </div>

        <DatePicker
          value={startDateRange}
          mode="range"
          onChange={handleStartDateRangeChange}
          locale={locale}
          placeholder={t("filters.createdDate")}
          isSmall
          width="min-w-40"
          icon={<Calendar2 size={16} color="currentColor" />}
          align="left"
        />

        <div className="min-w-40">
          <Select<string>
            options={statusOptions}
            placeholder={t("filters.allStatuses")}
            value={statusFilter}
            onChange={onStatusChange}
            isSmall
            icon={<Status size={16} color="currentColor" />}
            width="w-80"
          />
        </div>

        {hasActiveFilters && (
          <FilterResetButton label={t("filters.reset")} onClick={onReset} />
        )}
      </div>

      <div className="flex items-center gap-3">
        <ViewModeToggle
          value={view}
          onChange={onViewChange}
          tableLabel={t("view.table")}
          cardsLabel={t("view.cards")}
        />
      </div>
    </div>
  );
}
